const Controller = require("../common/Controller");
const Utils = require("common/Utils");

const INVALID_CHARS_REGEX = /[\\\/:\*\?"<>\|]/;

/**
 * @class
 * @constructor
 */
function VFSController(model, transferManager, directoryListViewParent, directoryListViewChild, fileActionsView, createFolderDialog, renameDialog) {
    Controller.call(this);

    const self = this;
    let currentPath;
    let currentFile;
    let vfsId;
    let initialLoad = true;
    let suppressErrorMessages = false;
    let openOnTransferCompletion = false;
    let navigationManager;

    const views = [
        directoryListViewParent,
        directoryListViewChild,
        fileActionsView,
        createFolderDialog,
        renameDialog
    ];

    const ACCESS_DENIED = 5;
    const INVALID_CREDENTIALS = 8;
    const FILE_NOT_FOUND = 9;
    const FILE_ALREADY_EXISTS = 16;
    const DYNAMIC_APP_NOT_ENABLED = 21;

    const errorMessages = {
        [ACCESS_DENIED] : "common::vfs.accessDenied.message",
        [INVALID_CREDENTIALS] : "common::vfs.invalidCreds.title",
        [FILE_NOT_FOUND] : "common::vfs.fileNotFound.message",
        [FILE_ALREADY_EXISTS] : "common::vfs.fileAlreadyExists.message",
        [DYNAMIC_APP_NOT_ENABLED] : "common::dynamicApp.not.enabled"
    };

    const GENERAL_ERROR_MSG = "common::vfs.operationFailed.message";

    transferManager.getViews().forEach((view) => {
        views.push(view);
    });

    const super_setNavManager = this.setNavManager;
    this.setNavManager = function(navManager) {
        navigationManager = navManager;
        super_setNavManager.call(this, navManager);
    };

    /**
     * @override
     * @inheritDoc
     */
    this.getViews = function() {
        return views;
    };

    /**
     * @override
     * @inheritsDoc
     */
    this.getId = function() {
        return "VFSController";
    };

    function closeIfDynamicAppNotEnabled(errorCode) {
        if (errorCode === DYNAMIC_APP_NOT_ENABLED) {
            self.publishEvent("VFSController", "close-vfs");
        }
    }

    this.setPath = function(path, skipHistory) {
        currentPath = path;
        currentFile = undefined;
        const isRoot = (path === "/");

        let failed = false;
        const promises = [];
        const dir = model.setPath(path);
        const leafView = (isRoot ? directoryListViewParent : directoryListViewChild);

        // If we are showing a child directory, populate the parent view first
        if (!isRoot) {
            // Clear the current contents of the child view
            directoryListViewChild.clearContents();

            let parentDir = dir.getParent();
            directoryListViewParent.setTitle(parentDir.getName());
            let parentPromise = parentDir.getContents().then((contents) => {
                if (failed) {
                    return;
                }
                directoryListViewParent.setDirContents(parentDir, contents, false);
            });
            promises.push(parentPromise);
        }

        leafView.setTitle(dir.getName());

        // Show the correct view
        self.showView(leafView, skipHistory);
        adoptLoadingSpinner(leafView.getContent());

        let leafPromise = dir.getContents().then((contents) => {
            if (failed) {
                return;
            }
            leafView.setDirContents(dir, contents, false);
        });
        promises.push(leafPromise);

        return $.when.apply($, promises)
            .fail((response) => {
                failed = true;
                const json = response && response.responseJSON || {};
                const errorCode = json.code;
                const isAuthError = errorCode === INVALID_CREDENTIALS || json.msg === "Invalid username and password";
                const legacy = !Utils.isGreaterThanOrEqualToServerVersion(CudaLaunch.serverConfiguration.version, "7.1.0");

                if (initialLoad && isAuthError && legacy) {
                    setTimeout(() => {
                        relaunch(path);
                    }, 10);
                } else {
                    const messageKey = errorMessages[errorCode] || GENERAL_ERROR_MSG;
                    const message = $.t(messageKey);

                    if (errorCode === FILE_NOT_FOUND || errorCode === ACCESS_DENIED) {
                        handlePathNavigationFailure(errorCode);
                    } else {
                        // Not the best way of doing this, but there's too many things going on in the background
                        // and not all of them are promise based. So we can't wait for promise resolution.
                        setTimeout(() => {
                            CudaLaunch.controllers.RootController.showErrorDialog(message, false, undefined, () => {
                                // Check if the failure was for invalid creds -- if so, re-prompt
                                if (isAuthError) {
                                    relaunch(path);
                                } else {
                                    closeIfDynamicAppNotEnabled(errorCode);
                                }
                            });
                        }, 50);
                    }
                }
            }).always(() => {
                initialLoad = false;
                releaseLoadingSpinner();
            }).then(() => {
                suppressErrorMessages = false;
                return dir;
            });
    };

    function relaunch(path) {
        $.ajax({
            url : "/sslvpn_api/launchPrerequisites",
            type : "GET",
            data : {
                resourceId : vfsId
            },
            cache : false
        }).done((prerequisites) => {
            let attributesMap = {};

            if (Array.isArray(prerequisites.attributes)) {
                if (prerequisites.attributes.length > 0) {
                    attributesMap["user"] = prerequisites.attributes; // For pre-7.1 servers wrap the response in a user namespace
                }
            } else {
                attributesMap = prerequisites.attributes;
            }

            // If there are any attributes to prompt for then do so, if not then must have been saved incorrectly so prompt for credentials
            if (!$.isEmptyObject(attributesMap)) {
                CudaLaunch.controllers.LaunchAttributesController.resolveAttributes(attributesMap, prerequisites.launchId, () => {
                    self.setPath(path);
                }, $.t("common::auth.prompt.title"), false);
            } else {
                CudaLaunch.controllers.AuthenticationController.resolveCredentials(vfsId, () => {
                    self.setPath(path);
                }, $.t("common::auth.prompt.title"));
            }
        });
    }

    /**
     * @override
     * @inheritDoc
     */
    this.getState = function() {
        return {
            file : (currentFile) ? currentFile.name : undefined,
            path : currentPath
        };
    };

    /**
     * @override
     * @inheritDoc
     */
    this.setState = function(state) {
        if (!vfsId) {
            vfsId = CudaLaunch.NP.vfsId;
            model.init(vfsId);
        }

        let parentPath = state.path;

        if (state.file) {
            let pathEnd = Math.max(1, state.path.lastIndexOf("/"));
            parentPath = state.path.substr(0, pathEnd);
        }

        this.setPath(parentPath, true).then((dir) => {
            if (state.file) {
                let file = dir.getFile(state.file);

                if (file) {
                    self.publishEvent(self.getId(), "show-file-actions", {
                        path : state.path,
                        file : file,
                        skipHistory : true
                    });
                } else {
                    // Failed to find file
                    handlePathNavigationFailure(FILE_NOT_FOUND);
                }
            }
        });
    };

    function handlePathNavigationFailure(errorCode) {
        if (!suppressErrorMessages) {
            CudaLaunch.controllers.RootController.showErrorDialog($.t(errorMessages[errorCode]), false, undefined, () => {
                suppressErrorMessages = true;
                self.showPreviousView();
            });
        } else {
            self.showPreviousView();
        }
    }

    self.subscribeTo("launch-resource", (data) => {
        vfsId = data.vfsId;

        if (data.requiresInit) {
            initialLoad = true;
            directoryListViewParent.clearContents();
            directoryListViewChild.clearContents();

            model.init(data.vfsId);
            self.publishEvent(self.getId(), "show-directory-list", {
                path : "/"
            });
        }
    });

    self.subscribeTo("show-file-actions", (data) => {
        let uri = buildFileUri(data.path);
        transferManager.getTransferDetails(uri).then((transferDetails) => {
            if (transferDetails.progress == undefined) {
                transferDetails.progress = ((transferDetails.transferred / data.file.size) * 100).toFixed(0);
            }
            fileActionsView.setFile(data.file, data.path, transferDetails);

            // If not currently in root and navigating to a sibling then repopulate the parent list view
            if (!currentFile && currentPath != "/" && data.path.indexOf(currentPath) == -1) {
                let dir = model.getDirectory(currentPath).getParent();

                if (dir.getName() != "") {
                    dir.getContents().then((contents) => {
                        directoryListViewChild.setDirContents(dir, contents, false);
                    });
                    directoryListViewChild.setTitle(dir.getName());
                }
            }
            currentPath = data.path;
            currentFile = data.file;
            self.showView(fileActionsView, data.skipHistory);
        });
    });

    self.subscribeTo("show-directory-list", (data) => {
        currentFile = undefined;
        suppressErrorMessages = false;
        self.setPath(data.path);
    });

    self.subscribeTo("download-file", (data) => {
        let uri = buildFileUri(currentPath);

        openOnTransferCompletion = !!data.autoOpen;
        transferManager.getTransferDetails(uri).then((transferDetails) => {
            if (openOnTransferCompletion && transferDetails.status == "completed") {
                transferManager.open(uri);
            } else {
                transferManager.download(uri, currentFile.name);
            }
        });
    });

    self.subscribeTo("upload-files", (data) => {
        transferManager.upload(vfsId, data.targetDir);
    });

    self.subscribeTo("show-create-folder-dialog", (data) => {
        createFolderDialog.setPath(data.targetDir);
        self.showView(createFolderDialog, true);
    });

    self.subscribeTo("create-folder", (data) => {
        // Check for invalid characters
        if (data.name == "" || INVALID_CHARS_REGEX.test(data.name)) {
            createFolderDialog.setErrorMessage($.t("common::vfs.invalidFilename.message"));
            return;
        }

        createFolderDialog.setEnabled(false);

        let newPath = data.target + data.name;
        let dialogContent = createFolderDialog.getContent();

        model.createFolder(newPath)
            .done(() => {
                model.setPath(newPath)
                    .getParent()
                    .refresh()
                    .then(() => {
                        self.setPath(newPath, false);
                    });
            }).fail((jqXHR) => {
                let errorMessage = errorMessages[jqXHR.responseJSON.code] || GENERAL_ERROR_MSG;

                dialogContent.one("popupafterclose", () => {
                    CudaLaunch.controllers.RootController.showErrorDialog($.t(errorMessage), false, undefined,
                        () => {
                            closeIfDynamicAppNotEnabled(jqXHR.responseJSON.code);
                        }
                    );
                });
            }).always(() => {
                dialogContent.popup("close");
                createFolderDialog.setEnabled(true);
            });
    });

    self.subscribeTo("delete-file-folder", (data) => {
        let options = {
            yes : $.t("common::yes"),
            no : $.t("common::no")
        };

        CudaLaunch.controllers.RootController.showPromptDialog($.t("common::vfs.deleteConfirmation.title"), $.t("common::vfs.deleteConfirmation.message"), options)
            .then(function(val) {
                if (val == "yes") {
                    this.setEnabled(false);

                    model.delete(data.target)
                        .then(() => {
                            let parentDeleted = (data.target != currentPath);
                            self.showPreviousView();

                            if (parentDeleted) {
                                self.showPreviousView();
                            }
                        })
                        .fail((jqXHR) => {
                            let errorMessage = (jqXHR.responseJSON)
                                ? errorMessages[jqXHR.responseJSON.code] || GENERAL_ERROR_MSG
                                : GENERAL_ERROR_MSG;
                            CudaLaunch.controllers.RootController.showErrorDialog($.t(errorMessage), false, undefined,
                                () => {
                                    closeIfDynamicAppNotEnabled(jqXHR.responseJSON.code);
                                }
                            );
                        });
                }
            });
    });

    self.subscribeTo("show-rename-dialog", (data) => {
        renameDialog.setPath(data.target);
        self.showView(renameDialog, true);
    });

    self.subscribeTo("rename-file-folder", (data) => {
        // Check for invalid characters
        if (data.newName == "" || INVALID_CHARS_REGEX.test(data.newName)) {
            renameDialog.setErrorMessage($.t("common::vfs.invalidFilename.message"));
            return;
        }

        if (data.newName == data.oldName) {
            // Name is unchanged -- just close the dialog
            renameDialog.getContent().popup("close");
        } else {
            let errorMessage;
            let errorCode = 0;
            renameDialog.setEnabled(false);

            let oldPath = data.path + data.oldName;
            let newPath = data.path + data.newName;

            model.rename(oldPath, newPath)
                .then(() => {
                    if (oldPath == currentPath) {
                        if (currentFile) {
                            // Current file renamed
                            self.setPath(data.path, true)
                                .then((dir) => {
                                    self.publishEvent(self.getId(), "show-file-actions", {
                                        path : newPath,
                                        file : dir.getFile(data.newName),
                                        skipHistory : false
                                    });
                                });
                        } else {
                            // Current dir renamed
                            self.setPath(newPath, false);
                        }
                    } else {
                        // Parent renamed
                        if (currentFile) {
                            let filename = currentFile.name;

                            self.setPath(newPath, true)
                                .then((dir) => {
                                    self.publishEvent(self.getId(), "show-file-actions", {
                                        path : newPath + "/" + filename,
                                        file : dir.getFile(filename),
                                        skipHistory : true
                                    });
                                });
                        } else {
                            let currentDirName = currentPath.substring(currentPath.lastIndexOf("/"));
                            self.setPath(newPath + currentDirName, true);
                        }

                        // We need to update all the entries in our nav history to refer to
                        // the renamed path
                        navigationManager.updateVFSPaths(oldPath, newPath);
                    }
                }).fail((jqXHR) => {
                    errorMessage = (jqXHR.responseJSON)
                        ? errorMessages[jqXHR.responseJSON.code] || GENERAL_ERROR_MSG
                        : GENERAL_ERROR_MSG;
                    errorCode = jqXHR.responseJSON.code;
                }).always(() => {
                    if (errorMessage) {
                        renameDialog.getContent().one("popupafterclose", () => {
                            CudaLaunch.controllers.RootController.showErrorDialog($.t(errorMessage), false, undefined,
                                () => {
                                    closeIfDynamicAppNotEnabled(errorCode);
                                }
                            );
                        });
                    }

                    renameDialog.getContent().popup("close");
                });
        }
    });

    self.subscribeTo("transfer-progress-update", (data) => {
        let uri = buildFileUri(currentPath);

        // The update is for the file that we are currently showing
        if (data.transferId === uri) {
            if (data.progress == undefined) {
                data.progress = ((data.transferred / currentFile.size) * 100).toFixed(0);
            } else {
                data.progress = parseInt(data.progress).toFixed(0);
            }

            fileActionsView.setDownloadProgress(data, true);

            if (data.status === "failed") {
                let message = data.errorMsg || $.t("common::vfs.downloadFailed.title");
                CudaLaunch.controllers.RootController.showErrorDialog(message, false, undefined, () => {
                    fileActionsView.setDownloadProgress(data, true);

                    // If error was dynamicApp.not.enabled, close the VFS view
                    if (data.errorMsg === $.t(errorMessages[DYNAMIC_APP_NOT_ENABLED])) {
                        self.publishEvent("VFSController", "close-vfs");
                    }
                });
            } else if (data.status === "cancelled") {
                if (data.errorResponse) {
                    let errorResponse = JSON.parse(data.errorResponse);

                    // If cancelled due to dynamicApp.not.enabled response, show an error and then close the VFS view
                    if (errorResponse.result === "ERROR_MSG" && errorResponse.code === DYNAMIC_APP_NOT_ENABLED) {
                        CudaLaunch.controllers.RootController.showErrorDialog($.t(errorMessages[DYNAMIC_APP_NOT_ENABLED]), false, undefined, () => {
                            closeIfDynamicAppNotEnabled(DYNAMIC_APP_NOT_ENABLED);
                        });
                    }
                }
            } else if (openOnTransferCompletion && data.status === "completed") {
                transferManager.open(data.transferId);
            }
        } else if (data.status === "failed") {
            // Send a notification since we can't show anything in the UI because we
            // aren't showing the affected item anymore
            let title = (data.transferType === "download")
                ? $.t("common::vfs.downloadFailed.title")
                : $.t("common::vfs.uploadFailed.title");
            self.publishEvent("Electron", "show-notification", {title : title, body : data.filename});
        } else if (data.targetDir && data.status === "completed" && data.transferType === "upload") {
            let uploadPath = (data.targetDir == "/")
                ? "/"
                : data.targetDir.substring(0, data.targetDir.length - 1);

            refreshListViews(uploadPath);
        }
    });

    self.subscribeTo("np-debug", (data) => {
        console.debug("Network Places debug message: " + JSON.stringify(data));
    });

    function refreshListViews(updatedPath) {
        // Check if we are showing the affected directory and refresh it
        let dir;
        let promise;

        if (updatedPath == "/" && currentPath.lastIndexOf("/") == 0) {
            // Refresh root
            dir = model.getDirectory(updatedPath);
            promise = dir.refresh().then((contents) => {
                directoryListViewParent.setDirContents(dir, contents, false);
            });
        } else if (updatedPath == currentPath) {
            // Refresh child
            dir = model.getDirectory(updatedPath);
            promise = dir.refresh().then((contents) => {
                directoryListViewChild.setDirContents(dir, contents, false);
            });
        } else if ((currentPath.substring(0, updatedPath.length) == updatedPath) &&
            (currentPath.substring(updatedPath.length + 1).indexOf("/") == -1)) {
            // Refresh parent
            dir = model.getDirectory(updatedPath);
            promise = dir.refresh().then((contents) => {
                if (currentFile) {
                    directoryListViewChild.setDirContents(dir, contents, false);
                } else {
                    directoryListViewParent.setDirContents(dir, contents, false);
                }
            });
        } else {
            return new $.Deferred().reject().promise();
        }

        return promise.then(() => {
            return dir;
        }).fail((jqXHR) => {
            let errorMessage = errorMessages[jqXHR.responseJSON.code] || GENERAL_ERROR_MSG;
            CudaLaunch.controllers.RootController.showErrorDialog($.t(errorMessage), false, undefined,
                () => {
                    closeIfDynamicAppNotEnabled(jqXHR.responseJSON.code);
                }
            );
        });
    }

    self.subscribeTo("cancel-transfer", (data) => {
        transferManager.cancel(data.transferId);
    });

    self.subscribeTo("refresh-directory-list", (data) => {
        if (data && data.target) {
            refreshListViews(data.target);
        } else {
            let dirRefresh;
            let lastSlashIndex = currentPath.lastIndexOf("/");

            if (lastSlashIndex == 0) {
                dirRefresh = refreshListViews("/");

                if (!currentFile && currentPath != "/") {
                    refreshListViews(currentPath);
                }
            } else {
                if (!currentFile) {
                    refreshListViews(currentPath);
                }

                let path = currentPath.substring(0, lastSlashIndex);
                dirRefresh = refreshListViews(path);
            }

            if (currentFile) {
                // Re-show the file actions view in case the file details have been updated by the refresh
                dirRefresh.then((dir) => {
                    let file = dir.getFile(currentFile.name);

                    if (file) {
                        self.publishEvent(self.getId(), "show-file-actions", {
                            path : currentPath,
                            file : file,
                            skipHistory : false
                        });
                    } else {
                        CudaLaunch.controllers.RootController.showErrorDialog($.t("common::vfs.fileNotFound.message"), false, undefined, self.showPreviousView);
                    }
                });
            }
        }
    });

    $(document).on("columnScrollStop", (event, column, view) => {
        if (column && view) {
            // Was this triggered by something we care about?
            if (/^VFSDirectoryList[0-9]+$/.test(view.getHashName())) {
                let $column = $(column);

                // Are we approaching the end of the list
                if ($column.scrollTop() + $column.height() > $column.prop("scrollHeight") * 0.9) {
                    adoptLoadingSpinner(view.getContent());

                    let path = view.getPath();
                    let dir = model.getDirectory(path);
                    dir.fetch().done((contents) => {
                        if (contents.length > 0) {
                            view.setDirContents(dir, contents, true);
                        }
                    }).always(releaseLoadingSpinner);
                }
            }
        }
    });

    function adoptLoadingSpinner(target) {
        let loadingSpinner = $(".ui-loader").detach();
        loadingSpinner.addClass("inline");
        loadingSpinner.appendTo(target.closest(".scrollContent"));
    }

    function releaseLoadingSpinner() {
        let loadingSpinner = $(".ui-loader").detach();
        loadingSpinner.loader("hide");
        $("body").append(loadingSpinner);
        loadingSpinner.removeClass("inline");
    }

    function buildFileUri(path) {
        return App.getRemoteUrlPrefix() + "/sslvpn_api/vfs/download?vfsId=" + encodeURIComponent(vfsId) + "&target=" + encodeURIComponent(path);
    }
}

Utils.inherits(VFSController, Controller);

module.exports = VFSController;
